import React, { useRef } from 'react';
import { Button } from '@chakra-ui/react';
import MonacoEditor from '@monaco-editor/react';
import AutoSizer from 'react-virtualized-auto-sizer';
import { twMerge } from 'tailwind-merge';
import { TbFileTypeSql } from 'react-icons/tb';

import { ConnectionForm, SkippableModal } from './components';
import useMigrationViewModel from './NewMigration.viewmodel';
import Selecto from "react-selecto";
import { FixedSizeList } from 'react-window';

const STATEMENT_STATUS_CLASSES = {
    success: 'bg-green-600 text-white',
    error: 'bg-red-600 text-white',
    exec: 'bg-yellow-400 text-white'
};

const Row = ({ index, style, data }: { index: number, style: React.CSSProperties, data: any }) => {
    const { files, selectedForRun, selectedFile, handleItemClick } = data;
    const file = files[index];
    const isSelectedForRun = selectedForRun.includes(file.s3_guid);
    const isSelectedForView = selectedFile?.s3_guid === file.s3_guid;

    return (
        <div
            style={style}
            className={twMerge(
                'file-item flex items-center px-3 select-none', // class สำหรับ Selecto
                isSelectedForRun && 'bg-blue-300 text-white',
                isSelectedForView && 'ring-2 ring-blue-500 z-10'
            )}
            data-key={file.s3_guid}
            onClick={(e) => handleItemClick(e, file, index)}
        >
            <TbFileTypeSql className="mr-2 flex-shrink-0" />
            <span className="truncate cursor-pointer flex-grow">{file.file_name}</span>
        </div>
    );
};

function NewMigration() {
    const {
        files,
        outputLogs,
        showSkippableModal,
        error,
        selectedFile,
        skipResolverRef,
        virtualTerminalRef,
        selectedFileContent,
        selectedForRun,
        setSelectedForRun,

        handleFileSelect,
        handleClickConnect,
        handleClickExportRunningLog,
        handleClickRunSelectedFiles,
        handleClickRunAllFiles,
        setShowSkippableModal
    } = useMigrationViewModel();

    const lastSelectedIndexRef = useRef<number | null>(null);
    const listContainerRef = useRef<HTMLDivElement>(null);

    const handleItemClick = (event: React.MouseEvent, file: any, index: number) => {
        handleFileSelect(file);

        const isShiftPressed = event.nativeEvent.shiftKey;
        const isCtrlPressed = event.nativeEvent.ctrlKey || event.nativeEvent.metaKey;

        if (isShiftPressed && lastSelectedIndexRef.current !== null) {
            const start = Math.min(lastSelectedIndexRef.current, index);
            const end = Math.max(lastSelectedIndexRef.current, index);
            const rangeIds = files.slice(start, end + 1).map(f => f.s3_guid);

            const newSelection = new Set([...selectedForRun, ...rangeIds]);
            setSelectedForRun(Array.from(newSelection));

        } else if (isCtrlPressed) {
            const newSelection = new Set(selectedForRun);
            if (newSelection.has(file.s3_guid)) {
                newSelection.delete(file.s3_guid);
            } else {
                newSelection.add(file.s3_guid);
            }
            setSelectedForRun(Array.from(newSelection));
            lastSelectedIndexRef.current = index;

        } else {
            setSelectedForRun([file.s3_guid]);
            lastSelectedIndexRef.current = index;
        }
    };

    return (
        <React.Fragment>
            <div className="flex h-full flex-col">
                <ConnectionForm onClickConnect={handleClickConnect} />
                <div className="mt-[16px] flex gap-2">
                    <Button
                        size="sm"
                        onClick={handleClickRunSelectedFiles}
                    >
                        Run Selected
                    </Button>
                    <Button
                        size="sm"
                        onClick={handleClickRunAllFiles}
                    >
                        Run All
                    </Button>
                </div>
                <div className="flex flex-1 px-2 pt-2" style={{ height: 'calc(100vh - 150px)' }}>
                    <div ref={listContainerRef} className="w-[350px] border flex flex-col relative">
                        <AutoSizer>
                            {({ height, width }) => (
                                <FixedSizeList
                                    height={height}
                                    width={width}
                                    itemCount={files.length}
                                    itemSize={36} // ความสูงของแต่ละแถว
                                    itemData={{
                                        files,
                                        selectedForRun,
                                        selectedFile,
                                        handleItemClick,
                                    }}
                                >
                                    {Row}
                                </FixedSizeList>
                            )}
                        </AutoSizer>
                    </div>

                    <div className="flex flex-1 flex-col">
                        <div className="flex-1 border">
                            <MonacoEditor
                                language="sql"
                                theme="vs-light"
                                value={selectedFileContent}
                                options={{
                                    selectOnLineNumbers: true,
                                    automaticLayout: true,
                                }}
                            />
                        </div>
                        <div
                            ref={virtualTerminalRef}
                            className="h-[200px] overflow-y-auto border p-2 relative"
                        >
                            <Button
                                size="sm"
                                type="button"
                                onClick={handleClickExportRunningLog}
                                className="absolute right-10 h-8"
                            >
                                Export
                            </Button>
                            <div id="output-log-container">
                                {outputLogs.map(log => (
                                    <p>
                                        <span
                                            className={twMerge(
                                                STATEMENT_STATUS_CLASSES[log.detail.status],
                                                'rounded-md px-2'
                                            )}
                                        >
                                            [{log.detail.status}]
                                        </span>
                                        <span>
                                            [{log.folder}/{log.id}]
                                        </span>
                                        : <span>{log.detail.message}</span>
                                        {log.detail.status && (
                                            <span
                                                className='px-2'
                                            >
                                                [Elapsed Time - {log.detail.elapsed_time}]
                                            </span>
                                        )}
                                    </p>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <Selecto
                dragContainer={"body"}
                selectableTargets={[".file-item"]}
                selectByClick={false}
                toggleContinueSelect={["shift", "ctrl"]}
                keyContainer={window}
                hitRate={0}
                scrollOptions={{
                    container: listContainerRef.current!,
                    throttleTime: 30,
                    threshold: 0,
                }}
                onSelect={e => {
                    const newSelection = new Set(selectedForRun);
                    e.added.forEach(el => newSelection.add(el.dataset.key!));
                    e.removed.forEach(el => newSelection.delete(el.dataset.key!));
                    setSelectedForRun(Array.from(newSelection));
                }}
            />

            <SkippableModal
                noCloseButton={true}
                isOpen={showSkippableModal}
                onClose={setShowSkippableModal.off}
                error={error}
                skipResolverRef={skipResolverRef}
            />
        </React.Fragment>
    );
}

export default NewMigration;